import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Promotion, OrderApiException, OrderErrorCode } from '@/models';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { threadId } from 'worker_threads';
import { AccountManagementEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';

@Component({
  name: 'CmgMemberRewardItem',
})
export default class CmgMemberRewardItem extends Vue {
  isLoading = false;
  dateOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

  @Prop({ required: true, default: false, type: Object })
  reward!: Promotion;

  get defaultRewardChili(): string {
    return this.$appStore.state.cms.images.icons.defaultRewardChili.default;
  }

  get buttonText(): string {
    const order = this.$services.order.getCurrentOrder();
    if (!order || !order.discounts || !order.discounts.find(d => d.promotionCode === this.reward.promotionCode)) {
      return 'ADD TO BAG';
    } else {
      if (this.$appStore.state.ui.clientWidth >= 768) {
        return 'REMOVE FROM BAG';
      }
      return 'REMOVE';
    }
  }

  onTerms(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: this.reward.termsAndConditions });
    this.$services.ui.firstFocus();
  }

  onScan() {
    this.$services.ui.setModalVisibility('rewardQR', true, {
      reward: this.reward,
    });
  }

  async onAddToBag(event: any) {
    this.$services.ui.setLastSelectedElement('bag');
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: this.reward.promotionName + AnalyticsEventAction.RewardToBag,
      label: AnalyticsEventLabel.RewardToBag + this.reward.promotionName,
    });
    this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.RewardsAddedToBag, undefined, this.reward.promotionName, 'Rewards Wallet'));

    let order = this.$services.order.getCurrentOrder();
    if (!order) {
      if (this.$appStore.state.restaurant.selectedRestaurant) {
        order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant!.restaurantNumber);
      } else {
        this.$services.ui.setModalVisibility('findAChipotle', true, {
          onClose: async () => {
            if (!order || !this.$appStore.state.restaurant.selectedRestaurant) {
              return;
            }

            try {
              this.isLoading = true;

              order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber);

              if (order) {
                await this.$services.order.applyDiscountToOrder(order, this.reward.promotionCode);

                this.$services.bag.setBagVisibility(true);
              }
            } catch (e) {
              this.handleAddToBagException(e);
            } finally {
              this.isLoading = false;
            }
          },
        });

        this.$services.ui.setModalVisibility('toast', true, {
          body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
          bodyOnly: true,
        });
        Vue.nextTick(() => this.$services.ui.focusFirstDiv('.cmg-modal-container'));
        return;
      }
    }

    // If we STILL don't have an order available, bail out
    if (!order) {
      return;
    }

    try {
      if (!order.discounts || !order.discounts.find(d => d.promotionCode === this.reward.promotionCode)) {
        // this discount is not in the bag right now.
        this.isLoading = true;

        await this.$services.order.applyDiscountToOrder(order, this.reward.promotionCode);

        this.$services.bag.setBagVisibility(true);
        this.$nextTick(() => {
          Vue.nextTick(()=>this.$services.ui.focusDiv('.fullModal'));
          this.$services.ui.setLastSelectedElement('bag');
        });
      } else {
        // this discount was already in the bag.
        this.isLoading = true;

        await this.$services.order.removeDiscountFromOrder(order, this.reward.promotionCode);
        this.$services.bag.setBagVisibility(true);
        Vue.nextTick(()=>this.$services.ui.focusDiv('.fullModal'));
      }
    } catch (e) {
      this.handleAddToBagException(e);
    } finally {
      this.isLoading = false;
    }
  }

  private handleAddToBagException(e: any) {
    if (!(e instanceof OrderApiException)) {
      throw e;
    }

    const error = e.errors[0];
    if (!error) {
      throw e;
    }

    switch (error.errorCode) {
      case OrderErrorCode.RewardExceedsLimit:
        this.$services.ui.setModalVisibility('toast', true, {
          title: '',
          body: 'Only one reward per order.',
          bodyOnly: true,
        });
        Vue.nextTick(() => {
          setTimeout(() => {
            this.$services.ui.nextFocus('reward-heading');
          }, 6000);
        });
        break;
    }
  }
}
